import { Link } from 'react-router-dom'

import Card from '@/gf/components/Card'
import Spinner from '@/gf/components/Spinner'
import { Table, Tbody, Td, Th, Thead, Tr } from '@/gf/components/Table'
import Money from '@/gf/modules/Money'
import { MetricsVars } from '../../types'
import useGearflowShippingMetrics from '../hooks/useGearflowShippingMetrics'

const GearflowShippingMetrics = ({ fromDate, toDate, selectedStoreId }: MetricsVars) => {
  const { loading, error, data } = useGearflowShippingMetrics({
    fromDate,
    toDate,
    selectedStoreId,
  })

  return (
    <Card title="Gearflow shipping metrics">
      {error ? (
        <Card.Section title="Error fetching data">{`${error.message}`}</Card.Section>
      ) : loading || !data ? (
        <Card.Section>
          <Spinner />
        </Card.Section>
      ) : (
        <>
          <Card.Section title="Overview">
            <div className="text-sm text-gray-700 grid grid-cols-2 gap-2">
              <p>Net shipping:</p>
              <p>{Money.format(data.netShippingAmount)}</p>
              <p>Average net shipping (per Store Order):</p>
              <p>{Money.format(data.averageNetShippingAmount)}</p>
              <p>Number of Store Orders:</p>
              <p>{data.storeOrdersCount}</p>
            </div>
          </Card.Section>
          {!selectedStoreId && (
            <Card.Section title="Store breakdown">
              <Table className="rounded border border-gray-300">
                <Thead>
                  <Tr>
                    <Th>Store</Th>
                    <Th>Net shipping</Th>
                    <Th>Average net shipping (per Store Order)</Th>
                    <Th>Number of Store Orders</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {data.shippingPerStoreGroups
                    // Sort in ascending order by net shipping amount
                    .sort((a, b) =>
                      Money.toInt(Money.sub(a.netShippingAmount, b.netShippingAmount))
                    )
                    .map((shippingPerStoreGroup) => (
                      <Tr key={shippingPerStoreGroup.storeName}>
                        <Td>{shippingPerStoreGroup.storeName}</Td>
                        <Td>{Money.format(shippingPerStoreGroup.netShippingAmount)}</Td>
                        <Td>{Money.format(shippingPerStoreGroup.averageNetShippingAmount)}</Td>
                        <Td>{shippingPerStoreGroup.storeOrdersCount}</Td>
                      </Tr>
                    ))}
                </Tbody>
              </Table>
            </Card.Section>
          )}
          <Card.Section title="Store orders">
            <Table className="rounded border border-gray-300">
              <Thead>
                <Tr>
                  <Th>Net</Th>
                  <Th>Shipping amount</Th>
                  <Th>Label cost</Th>
                  <Th>Store</Th>
                  <Th>Store order</Th>
                </Tr>
              </Thead>
              <Tbody>
                {data.storeOrders.map(
                  ({ storeOrderId, labelCost, storeOrderShippingCost, storeName }) => (
                    <Tr key={storeOrderId}>
                      <Td>{Money.format(Money.sub(storeOrderShippingCost, labelCost))}</Td>
                      <Td>{Money.format(storeOrderShippingCost)}</Td>
                      <Td>{Money.format(labelCost)}</Td>
                      <Td>{storeName}</Td>
                      <Td>
                        <Link
                          className="col-span-2 text-blue-500 underline"
                          to={`/orders/${storeOrderId}`}
                        >
                          {storeOrderId}
                        </Link>
                      </Td>
                    </Tr>
                  )
                )}
              </Tbody>
            </Table>
          </Card.Section>
        </>
      )}
    </Card>
  )
}

export default GearflowShippingMetrics
